import React, {useState, useEffect, useCallback} from "react";
import { Dialog, Classes, Checkbox, Icon } from "@blueprintjs/core";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {isEmail} from 'validator';

// Components
import {blueColor} from "../common/libs/theme-lib";
import Button from "../common/components/button";
import TextInput from "../common/components/text-input";
import Tooltip from "../common/components/tooltip";
import FeaturesSelector from "./features-selector";

// Actions
import {updateUser} from "./users-action";

// Hooks
import useInputValidation from "../common/hooks/use-input-validation";
import SelectInput from "../common/components/select-input";

// Selectors
import {clientsSelector} from "../clients/clients-selectors";
// Component Selector

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        updateUser: (user, sendWelcomeEmail) => dispatch(updateUser(user, sendWelcomeEmail))
    }
};

// Component Selector
const newUserDialogPageSelector = createSelector(
    clientsSelector,
    (clients) => {
        return {
            clients: clients?.toJS()?.map(d => ({
                value: d._id,
                label: d.name
            })) || []
        }
    }
);

const UpdateUserDialog = (props) => {

    // Props
    const {isOpen, onClose, user} = props;

    // Redux
    const {updateUser} = useReduxDispatch();
    const {clients} = useSelector(state => newUserDialogPageSelector(state));

    // i18n
    const {t} = useTranslation("users");

    // Form state
    const [nameData, setName] = useInputValidation();
    const [clientData, setClient] = useInputValidation();

    const [surname, setSurname] = useState("");
    const [emailData, setEmail] = useInputValidation({
        validationFn: value => !!isEmail(value),
        errMessage: t("common:invalidEmail")
    });
    const [features, setFeatures] = useState([]);

    // state of checkbox send email
    const [userEmailChecked, setUserEmailChecked] = useState(false);

    // Can submit
    const [canSubmit, setCanSubmit] = useState(false);

    // Check canSubmit
    useEffect(() => {
        setCanSubmit(
            nameData.isValid &&
            emailData.isValid
        );
    }, [nameData, emailData]);

    const setFormData = useCallback((user) => {
        setName(user.name);
        setSurname(user.surnames);
        setEmail(user.email);
        user.type === "client" && setFeatures(user.features);
        user.type === "client" && setClient(user.client?._id);
    }, [setClient, setEmail, setName]);

    // Set form state when user data changes
    useEffect(() => setFormData(user), [user, setFormData]);

    const langOptions = [
        {
            value: "es",
            label: t("profile:langOptions.es")
        },
        {
            value: "en",
            label: t("profile:langOptions.en")
        }
    ];

    // set language selected
    const [selectedLanguage, setSelectedLanguage] = useState(user.lang ? user.lang : langOptions[0].value);

    // user language selected
    const selectedLanguageData = langOptions.find(langOptions => langOptions.value === selectedLanguage);

    return (
        <Dialog
            isOpen={isOpen}
            onClose={onClose}
            lazy={true}
            style={{
                backgroundColor: "white",
                paddingBottom: "40px"
            }}
        >
            <div
                style={{margin: "20px 40px"}}
                className={Classes.DIALOG_BODY}
            >
                <h3 style={{margin: "20px 0"}}>{t("editUserDialog.title")}</h3>
                <h4 style={{color: blueColor}}>{user.surnames ? user.name + ' ' + user.surnames : user.name}</h4>
                <p>
                    {user.type === "admin" ? <>{t("editUserDialog.text1")} <b> {t("userType.admin")} </b> </>: <></>} 
                </p>
                <div style={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between"
                }}>
                    <TextInput
                        label={t("editUserDialog.input.name")}
                        value={nameData.value}
                        setValue={setName}
                        style={inputStyle}
                        placeholder={t("editUserDialog.input.placeholder_name")}
                        validation
                        isValid={nameData.isValid}
                        errorMessage={nameData.errorMessage}
                    />
                    <TextInput
                        label={t("editUserDialog.input.surnames")}
                        value={surname}
                        setValue={setSurname}
                        style={inputStyle}
                        placeholder={t("editUserDialog.input.placeholder_surname")}
                        validation
                    />
                    <TextInput
                        label={t("editUserDialog.input.email")}
                        value={emailData.value}
                        setValue={setEmail}
                        style={inputStyle}
                        placeholder={t("newUserDialog.input.placeholder_email")}
                        validation
                        isValid={emailData.isValid}
                        errorMessage={emailData.errorMessage}
                    />
                    <div style={{
                        width: "calc(50% - 10px)",
                        display: "flex"
                    }}>                   
                        <Checkbox
                            label={t("sendEmail")}
                            checked={userEmailChecked}
                            onChange={() => setUserEmailChecked(!userEmailChecked)}
                            style={{
                                margin: "auto 10px auto 0",
                                fontSize: "12px"
                            }}
                        />
                        <div style={{margin: "auto"}}>
                            <Tooltip
                                position={"top-start"}
                                content={
                                    <div style={{
                                        maxWidth: "400px"
                                    }}>
                                        {t("tooltip.updateUserSendEmail")}
                                    </div>
                                }
                            >
                                <Icon icon="help" iconSize={20} />
                            </Tooltip>
                        </div>
                    </div>
                    {user.type === "client" &&
                        <> 
                            <SelectInput
                                disabled
                                label={t("newUserDialog.input.type")}    
                                placeholder={t(user.type === "client" ? "userType.client" : "userType.admin")}                
                                style={{...inputStyle}}
                            />
                            <SelectInput
                                options={clients}
                                label={t("newUserDialog.input.clients")}
                                placeholder={t("common:selectClientPlaceholder")}
                                onChange={(newSelectedClient) => setClient(newSelectedClient.value)}
                                value={clients.find(client => client.value === clientData.value)}
                                style={{...inputStyle}}
                            /> 
                        </>}          
                    {user.type === "client" && (
                        <FeaturesSelector features={features} onChange={setFeatures} />
                    )}
                    <SelectInput
                        options={langOptions}
                        label={t("profile:inputs.lang")}
                        onChange={(selectedLanguage) => setSelectedLanguage(selectedLanguage.value)}
                        value={selectedLanguageData}
                        style={{...inputStyle}}
                    />
                </div>
            </div>
            <div
                style={{margin: "20px 40px 0 40px"}}
                className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <Button
                        text={t("editUserDialog.input.cancel")}
                        action={() => {
                            onClose();
                            setFormData(user);
                        }}
                        intent={"cancel"}
                        style={{
                            marginRight: "15px",
                        }}
                    />
                    {user.type === "client" ? 
                        <Button
                            text={t("editUserDialog.input.save")}
                            action={() => {

                                let updatedUser = {
                                    _id: user._id,
                                    name: nameData.value,
                                    surnames: surname,
                                    email: emailData.value,
                                    lang: selectedLanguageData.value,
                                    client: clientData.value,
                                };

                                if (user.type === "client") {
                                    updatedUser = {
                                        ...updatedUser,
                                        features: features
                                    };
                                }

                                updateUser(updatedUser, userEmailChecked);

                                onClose();

                            }}
                            intent={"secondary"}
                            disabled={canSubmit}
                        /> : 
                        <Button
                            text={t("editUserDialog.input.save")}
                            action={() => {
                            
                            let updatedUser = {
                                _id: user._id,
                                name: nameData.value,
                                surnames: surname,
                                email: emailData.value,
                                lang: selectedLanguageData.value,
                            };

                            if (user.type === "client") {
                                updatedUser = {
                                    ...updatedUser,
                                    features: features
                                };
                            }

                            updateUser(updatedUser, userEmailChecked);

                            onClose();

                            }}
                            intent={"secondary"}
                            disabled={canSubmit}
                        />
                }
                    
                </div>
            </div>
        </Dialog>
    );
};

export default UpdateUserDialog;

const inputStyle = {
    marginBottom: "15px",
    width: "calc(50% - 10px)"
};